import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Stack,
  IconButton,
  Popover,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import {
  DateRange as DateRangeIcon,
  Today as TodayIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { format, startOfDay, endOfDay } from 'date-fns';
import DateRangeCalendar from '../garage-admin/DateRangeCalendar';

const DateRangePicker = ({ period, onPeriodChange, onCustomRangeChange, customDateRange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [customCalendarOpen, setCustomCalendarOpen] = useState(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePresetClick = (preset) => {
    if (preset === 'custom') {
      setCustomCalendarOpen(true);
      handleClose();
    } else {
      onPeriodChange(preset);
      handleClose();
    }
  };

  const handleCustomCalendarApply = (dateRange) => {
    const formatDate = (date) => format(date, 'dd-MMM-yyyy');
    onCustomRangeChange({
      startDate: startOfDay(dateRange.startDate),
      endDate: endOfDay(dateRange.endDate),
      displayText: `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`
    });
    setCustomCalendarOpen(false);
  };

  const getDateRangeText = () => {
    const now = new Date();
    switch (period) {
      case 'today':
        return 'Today';
      case 'week':
        return 'This Week';
      case 'month':
        return 'This Month';
      case 'year':
        return 'This Year';
      case 'custom':
        if (customDateRange && customDateRange.displayText) {
          return customDateRange.displayText;
        }
        return 'Custom Range';
      default:
        return 'This Month';
    }
  };

  const presets = [
    {
      label: 'Today',
      value: 'today',
      icon: <TodayIcon fontSize="small" />
    },
    {
      label: 'This Week',
      value: 'week',
      icon: <CalendarIcon fontSize="small" />
    },
    {
      label: 'This Month',
      value: 'month',
      icon: <CalendarIcon fontSize="small" />
    },
    {
      label: 'This Year',
      value: 'year',
      icon: <CalendarIcon fontSize="small" />
    },
    {
      label: 'Custom Range',
      value: 'custom',
      icon: <DateRangeIcon fontSize="small" />
    }
  ];

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<DateRangeIcon />}
        onClick={handleClick}
        sx={{
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.04)
          },
          '& .MuiButton-startIcon': {
            color: theme.palette.primary.main
          }
        }}
      >
        {getDateRangeText()}
      </Button>

      {/* Period Selection Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 300,
            maxWidth: 400
          }
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Select Date Range
          </Typography>
          
          <Stack spacing={1}>
            {presets.map((preset) => (
              <Button
                key={preset.value}
                variant={period === preset.value ? 'contained' : 'text'}
                startIcon={preset.icon}
                onClick={() => handlePresetClick(preset.value)}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none'
                }}
              >
                {preset.label}
              </Button>
            ))}
          </Stack>
        </Box>
      </Popover>

      {/* Custom Date Range Calendar Modal */}
      <Dialog
        open={customCalendarOpen}
        onClose={() => setCustomCalendarOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, md: 3 },
            m: { xs: 0, md: 2 },
            height: '100%',
            maxHeight: { xs: '100vh', md: '700px' },
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0
        }}>
          <Typography variant="body1" fontWeight="600" sx={{ fontSize: '1.25rem' }}>
            Select Custom Date Range
          </Typography>
          <IconButton 
            onClick={() => setCustomCalendarOpen(false)} 
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': { 
                backgroundColor: 'action.hover' 
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DateRangeCalendar
          startDate={customDateRange?.startDate || new Date()}
          endDate={customDateRange?.endDate || new Date()}
          onChange={handleCustomCalendarApply}
          onClose={() => setCustomCalendarOpen(false)}
        />
      </Dialog>
    </>
  );
};

export default DateRangePicker;